import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { browseCategoriesForDropdown, formatCount } from '../categoryLabels';
import { subcategorySlug } from '../categorySlug';
import { categoryBrowsePath, subcategoryBrowsePath } from '../categoryPaths';
import { trackSelectCategory, trackSelectSubcategory } from '../ga';
import './CategoryMenuDropdown.css';

function cx(...parts) {
  return parts.filter(Boolean).join(' ');
}

function categoryMenuAriaLabel(name, count, { includeCount = true } = {}) {
  if (!includeCount) {
    return name;
  }
  const formatted = formatCount(count);
  if (!formatted) {
    return name;
  }
  return `${name}, ${formatted} products`;
}

function CategoryMenuCount({ count, className, hidden = false }) {
  if (hidden) {
    return null;
  }
  const formatted = formatCount(count);
  if (!formatted) {
    return null;
  }
  return (
    <span className={cx('category-menu__count', className)} aria-hidden="true">
      {formatted}
    </span>
  );
}

function ChevronRightIcon() {
  return (
    <svg
      className="category-menu__chevron"
      width="16"
      height="16"
      viewBox="0 0 20 20"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M7.5 5L12.5 10L7.5 15"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CategorySubpanel({
  category,
  hrefForShop,
  hrefForSubcategory,
  onCategoryClick,
  onSubcategoryClick,
  readOnly = false,
}) {
  const subcategories = Array.isArray(category?.subcategories)
    ? category.subcategories
    : [];

  if (!category) {
    return (
      <div className="category-menu__subpanel category-menu__subpanel--empty">
        <p className="category-menu__subpanel-hint">
          Choose a category to browse subcategories.
        </p>
      </div>
    );
  }

  return (
    <div className="category-menu__subpanel">
      {readOnly ? (
        <div className="category-menu__subpanel-all category-menu__subpanel-all--static">
          <span className="category-menu__subpanel-all-text">
            All in {category.name}
          </span>
        </div>
      ) : (
        <Link
          to={hrefForShop(category.slug)}
          className="category-menu__subpanel-all"
          aria-label={categoryMenuAriaLabel(`All in ${category.name}`, category.count)}
          onClick={() => onCategoryClick(category.slug)}
        >
          <span className="category-menu__subpanel-all-text">
            View all in {category.name}
            {formatCount(category.count) && (
              <>
                {' · '}
                <CategoryMenuCount
                  count={category.count}
                  className="category-menu__count--inline"
                />
              </>
            )}
          </span>
          <ChevronRightIcon />
        </Link>
      )}
      {subcategories.length > 0 ? (
        <ul className="category-menu__sub-list">
          {subcategories.map((sub) => (
            <li key={sub.name}>
              {readOnly ? (
                <span
                  className="category-menu__sub-link category-menu__sub-link--static"
                  aria-label={categoryMenuAriaLabel(sub.name, sub.count, {
                    includeCount: false,
                  })}
                >
                  <span className="category-menu__row-label">{sub.name}</span>
                </span>
              ) : (
                <Link
                  to={hrefForSubcategory(sub, category.slug)}
                  className="category-menu__sub-link"
                  aria-label={categoryMenuAriaLabel(sub.name, sub.count)}
                  onClick={() => onSubcategoryClick(category.slug, sub.name)}
                >
                  <span className="category-menu__row-label">{sub.name}</span>
                  <CategoryMenuCount count={sub.count} />
                </Link>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className="category-menu__subpanel-hint">
          {readOnly
            ? 'No subcategories in this category.'
            : 'Browse all products in this category.'}
        </p>
      )}
    </div>
  );
}

function DesktopCategoryMenu({
  dropdownCategories,
  activeShopCategorySlug,
  activeDepartmentSlug,
  onActiveDepartmentChange,
  hrefForShop,
  hrefForSubcategory,
  onCategoryClick,
  onSubcategoryClick,
  readOnly = false,
}) {
  const activeDepartment =
    dropdownCategories.find((c) => c.slug === activeDepartmentSlug)
    ?? dropdownCategories[0]
    ?? null;

  return (
    <div className="category-menu__split">
      <div className="category-menu__departments">
        <ul className="category-menu__dept-list">
          {dropdownCategories.map((category) => {
            const isActive = activeDepartmentSlug === category.slug;
            const hasSubcategories =
              Array.isArray(category.subcategories)
              && category.subcategories.length > 0;
            const rowClass = cx(
              'category-menu__dept-link',
              isActive && 'category-menu__dept-link--focused',
              activeShopCategorySlug === category.slug
                && 'category-menu__dept-link--active',
              readOnly && 'category-menu__dept-link--static',
            );
            const rowBody = (
              <>
                <span className="category-menu__row-label">{category.name}</span>
                <span className="category-menu__dept-meta">
                  <CategoryMenuCount count={category.count} hidden={readOnly} />
                  {hasSubcategories && <ChevronRightIcon />}
                </span>
              </>
            );

            return (
              <li key={category.slug}>
                {readOnly ? (
                  <button
                    type="button"
                    className={rowClass}
                    aria-label={categoryMenuAriaLabel(category.name, category.count, {
                      includeCount: false,
                    })}
                    aria-pressed={isActive}
                    onMouseEnter={() => onActiveDepartmentChange(category.slug)}
                    onFocus={() => onActiveDepartmentChange(category.slug)}
                    onClick={() => onActiveDepartmentChange(category.slug)}
                  >
                    {rowBody}
                  </button>
                ) : (
                  <Link
                    to={hrefForShop(category.slug)}
                    className={rowClass}
                    aria-label={categoryMenuAriaLabel(category.name, category.count)}
                    onMouseEnter={() => onActiveDepartmentChange(category.slug)}
                    onFocus={() => onActiveDepartmentChange(category.slug)}
                    onClick={() => onCategoryClick(category.slug)}
                  >
                    {rowBody}
                  </Link>
                )}
              </li>
            );
          })}
        </ul>
      </div>
      <CategorySubpanel
        category={activeDepartment}
        hrefForShop={hrefForShop}
        hrefForSubcategory={hrefForSubcategory}
        onCategoryClick={onCategoryClick}
        onSubcategoryClick={onSubcategoryClick}
        readOnly={readOnly}
      />
    </div>
  );
}

function MobileDepartmentsStep({
  dropdownCategories,
  activeShopCategorySlug,
  hrefForShop,
  onOpenDepartment,
  onCategoryClick,
  readOnly = false,
}) {
  return (
    <ul className="category-menu__mobile-list">
      {dropdownCategories.map((category) => {
        const hasSubcategories =
          Array.isArray(category.subcategories)
          && category.subcategories.length > 0;
        const rowClass = cx(
          'category-menu__mobile-row',
          activeShopCategorySlug === category.slug
            && 'category-menu__mobile-row--active',
        );

        if (hasSubcategories || readOnly) {
          return (
            <li key={category.slug}>
              <button
                type="button"
                className={rowClass}
                aria-label={categoryMenuAriaLabel(category.name, category.count, {
                  includeCount: !readOnly,
                })}
                onClick={() => onOpenDepartment(category.slug)}
              >
                <span className="category-menu__row-label">{category.name}</span>
                <span className="category-menu__dept-meta">
                  <CategoryMenuCount count={category.count} hidden={readOnly} />
                  {hasSubcategories && <ChevronRightIcon />}
                </span>
              </button>
            </li>
          );
        }

        return (
          <li key={category.slug}>
            <Link
              to={hrefForShop(category.slug)}
              className={cx(rowClass, 'category-menu__mobile-row--link')}
              aria-label={categoryMenuAriaLabel(category.name, category.count)}
              onClick={() => onCategoryClick(category.slug)}
            >
              <span className="category-menu__row-label">{category.name}</span>
              <CategoryMenuCount count={category.count} />
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

function MobileSubcategoriesStep({
  category,
  hrefForShop,
  hrefForSubcategory,
  onCategoryClick,
  onSubcategoryClick,
  readOnly = false,
}) {
  const subcategories = Array.isArray(category?.subcategories)
    ? category.subcategories
    : [];

  return (
    <>
      {readOnly ? (
        <div className="category-menu__mobile-link category-menu__mobile-link--all category-menu__mobile-link--static">
          <span className="category-menu__row-label">
            All in {category.name}
          </span>
        </div>
      ) : (
        <Link
          to={hrefForShop(category.slug)}
          className="category-menu__mobile-link category-menu__mobile-link--all"
          aria-label={categoryMenuAriaLabel(`All in ${category.name}`, category.count)}
          onClick={() => onCategoryClick(category.slug)}
        >
          <span className="category-menu__row-label">
            All in {category.name}
            {formatCount(category.count) && (
              <>
                {' · '}
                <CategoryMenuCount
                  count={category.count}
                  className="category-menu__count--inline"
                />
              </>
            )}
          </span>
        </Link>
      )}
      <ul className="category-menu__mobile-list">
        {subcategories.map((sub) => (
          <li key={sub.name}>
            {readOnly ? (
              <span
                className="category-menu__mobile-row category-menu__mobile-row--static"
                aria-label={categoryMenuAriaLabel(sub.name, sub.count, {
                  includeCount: false,
                })}
              >
                <span className="category-menu__row-label">{sub.name}</span>
              </span>
            ) : (
              <Link
                to={hrefForSubcategory(sub, category.slug)}
                className="category-menu__mobile-row category-menu__mobile-row--link"
                aria-label={categoryMenuAriaLabel(sub.name, sub.count)}
                onClick={() => onSubcategoryClick(category.slug, sub.name)}
              >
                <span className="category-menu__row-label">{sub.name}</span>
                <CategoryMenuCount count={sub.count} />
              </Link>
            )}
          </li>
        ))}
      </ul>
    </>
  );
}

/**
 * @param {{
 *   browseCategories: unknown[],
 *   activeShopCategorySlug?: string,
 *   disabled?: boolean,
 *   readOnly?: boolean,
 *   preview?: boolean,
 *   modal?: boolean,
 *   triggerLabel?: string,
 *   triggerVariant?: 'button' | 'link',
 * }} props
 */
export function CategoryMenuDropdown({
  browseCategories,
  activeShopCategorySlug,
  disabled,
  readOnly = false,
  preview = false,
  modal = false,
  triggerLabel = 'Categories',
  triggerVariant = 'button',
}) {
  const [open, setOpen] = useState(preview);
  const [activeDepartmentSlug, setActiveDepartmentSlug] = useState(null);
  const [mobileStep, setMobileStep] = useState('departments');
  const [mobileDepartmentSlug, setMobileDepartmentSlug] = useState(null);
  const [isNarrowViewport, setIsNarrowViewport] = useState(false);
  const rootRef = useRef(null);
  const menuOpen = preview ? true : open;
  const asModal = modal || (isNarrowViewport && !preview);
  const useMobileNav = isNarrowViewport && !preview;

  const dropdownCategories = useMemo(
    () => browseCategoriesForDropdown(browseCategories, activeShopCategorySlug),
    [browseCategories, activeShopCategorySlug],
  );

  const mobileDepartment = useMemo(
    () => dropdownCategories.find((c) => c.slug === mobileDepartmentSlug) ?? null,
    [dropdownCategories, mobileDepartmentSlug],
  );

  function hrefForShop(slug) {
    return categoryBrowsePath(slug);
  }

  function hrefForSubcategory(sub, shopSlug) {
    return subcategoryBrowsePath(shopSlug, subcategorySlug(sub));
  }

  function closeMenu() {
    if (preview) {
      return;
    }
    setOpen(false);
  }

  function handleCategoryClick(slug) {
    if (readOnly) {
      return;
    }
    trackSelectCategory(slug, 'category_menu');
    closeMenu();
  }

  function handleSubcategoryClick(categorySlug, subcategoryName) {
    if (readOnly) {
      return;
    }
    trackSelectSubcategory(categorySlug, subcategoryName, 'category_menu');
    closeMenu();
  }

  function openMobileDepartment(slug) {
    setMobileDepartmentSlug(slug);
    setMobileStep('subcategories');
  }

  function backToMobileDepartments() {
    setMobileStep('departments');
    setMobileDepartmentSlug(null);
  }

  useEffect(() => {
    const mobileQuery = window.matchMedia('(max-width: 768px)');
    const sync = () => setIsNarrowViewport(mobileQuery.matches);
    sync();
    mobileQuery.addEventListener('change', sync);
    return () => mobileQuery.removeEventListener('change', sync);
  }, []);

  useEffect(() => {
    if (preview || !menuOpen || !asModal) {
      return undefined;
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [asModal, menuOpen, preview]);

  useEffect(() => {
    if (preview || asModal) {
      return undefined;
    }
    function handlePointerDown(e) {
      if (!rootRef.current?.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
    };
  }, [preview, asModal]);

  useEffect(() => {
    if (preview || !menuOpen) {
      return undefined;
    }
    function handleKey(e) {
      if (e.key === 'Escape') {
        if (useMobileNav && mobileStep === 'subcategories') {
          backToMobileDepartments();
          return;
        }
        setOpen(false);
      }
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [useMobileNav, mobileStep, menuOpen, preview]);

  useEffect(() => {
    if (!menuOpen) {
      setActiveDepartmentSlug(null);
      setMobileStep('departments');
      setMobileDepartmentSlug(null);
      return;
    }

    const defaultSlug =
      activeShopCategorySlug
      || dropdownCategories[0]?.slug
      || null;
    setActiveDepartmentSlug(defaultSlug);
    setMobileStep('departments');
    setMobileDepartmentSlug(null);
  }, [menuOpen, activeShopCategorySlug, dropdownCategories]);

  const modalTitle =
    useMobileNav && mobileStep === 'subcategories' && mobileDepartment
      ? mobileDepartment.name
      : 'Categories';

  return (
    <div
      className={cx(
        'category-menu',
        preview && 'category-menu--preview',
        asModal && menuOpen && 'category-menu--modal',
      )}
      ref={rootRef}
    >
      {!preview && (
        <button
          type="button"
          className={cx(
            'category-menu__trigger',
            triggerVariant === 'link' && 'category-menu__trigger--link',
          )}
          aria-expanded={menuOpen}
          aria-haspopup={asModal ? 'dialog' : 'true'}
          aria-controls="category-menu-panel"
          id="category-menu-button"
          disabled={disabled}
          onClick={() => setOpen((v) => !v)}
        >
          <span className="category-menu__trigger-text">{triggerLabel}</span>
        </button>
      )}
      {menuOpen && (
        <>
          {!preview && (
            <button
              type="button"
              className="category-menu__backdrop"
              aria-hidden="true"
              tabIndex={-1}
              onClick={closeMenu}
            />
          )}
          <div
            className="category-menu__panel"
            id="category-menu-panel"
            role={asModal ? 'dialog' : 'region'}
            aria-modal={asModal ? 'true' : undefined}
            aria-label={preview ? 'Available categories' : undefined}
            aria-labelledby={
              preview
                ? undefined
                : asModal
                  ? 'category-menu-title'
                  : 'category-menu-button'
            }
          >
            {!preview && (
              <div className="category-menu__modal-header">
                {useMobileNav && mobileStep === 'subcategories' ? (
                  <button
                    type="button"
                    className="category-menu__back"
                    onClick={backToMobileDepartments}
                  >
                    <span aria-hidden="true">←</span>
                    Categories
                  </button>
                ) : null}
                <h2
                  id="category-menu-title"
                  className={cx(
                    'category-menu__modal-title',
                    useMobileNav
                      && mobileStep === 'subcategories'
                      && 'category-menu__modal-title--with-back',
                  )}
                >
                  {modalTitle}
                </h2>
                <button
                  type="button"
                  className="category-menu__close"
                  aria-label="Close categories"
                  onClick={closeMenu}
                >
                  <span aria-hidden="true">×</span>
                </button>
              </div>
            )}
            <nav className="category-menu__nav" aria-label="Product categories">
              {useMobileNav ? (
                mobileStep === 'departments' ? (
                  <MobileDepartmentsStep
                    dropdownCategories={dropdownCategories}
                    activeShopCategorySlug={activeShopCategorySlug}
                    hrefForShop={hrefForShop}
                    onOpenDepartment={openMobileDepartment}
                    onCategoryClick={handleCategoryClick}
                    readOnly={readOnly}
                  />
                ) : (
                  <MobileSubcategoriesStep
                    category={mobileDepartment}
                    hrefForShop={hrefForShop}
                    hrefForSubcategory={hrefForSubcategory}
                    onCategoryClick={handleCategoryClick}
                    onSubcategoryClick={handleSubcategoryClick}
                    readOnly={readOnly}
                  />
                )
              ) : (
                <DesktopCategoryMenu
                  dropdownCategories={dropdownCategories}
                  activeShopCategorySlug={activeShopCategorySlug}
                  activeDepartmentSlug={activeDepartmentSlug}
                  onActiveDepartmentChange={setActiveDepartmentSlug}
                  hrefForShop={hrefForShop}
                  hrefForSubcategory={hrefForSubcategory}
                  onCategoryClick={handleCategoryClick}
                  onSubcategoryClick={handleSubcategoryClick}
                  readOnly={readOnly}
                />
              )}
            </nav>
          </div>
        </>
      )}
    </div>
  );
}
